
import { computed, defineComponent } from 'vue';
import { useStore } from 'vuex';
import _ from 'lodash';
import { Getters, Mutations, userSettingsStore } from '@/store';
import ModalFooter from '@/modules/modals/components/ModalFooter.vue';
import ModalBody from '@/modules/modals/components/ModalBody.vue';
import ModalService from '@/modules/modals/services/modalService';
import Loader from '@/components/Loader.vue';
import SalesHistoryManager from '@/manager/2.6/SalesHistoryManager';
import Utils from '@/utils/QSUtils';
import ISalesHistoryProperty from '@/models/sales-history/ISalesHistoryProperty';
import GridColumnUtils from '@/utils/GridColumnUtils';
import ISaleData, { newSaleData } from '@/models/sales-history/ISaleData';
import ISalesHistorySelections, { newSalesHistorySelections } from '@/models/sales-history/ISalesHistorySelections';
import CompAddressUtils from '@/models/comp/CompAddressUtils';
import DateTimeUtils from '@/utils/DateTimeUtils';
import { SourceDataInitials } from '@/constants/SourceDataInitials';
import { newUpsertCompDataRequestByFieldName } from '@/store/2.6/models/IUpsertCompDataRequest';
import LogUsageManager from '@/manager/LogUsageManager';

export default defineComponent({
    components: { ModalBody, ModalFooter, Loader },
    data() {
        return {
            modal: null,
            saleHistoryFetched: false,
            isLoading: false,
            saleHistoryList: [] as any,
            excludeNonArmsLength: false,
            includeInAddenda: false,
            includeAnalysis: false,
            analysisOfPriorSales: '',
        };
    },
    setup() {
        const store = useStore();

        const computedDaActiveComp = computed(() => store.getters[Getters.CompAddresses.ActiveComp]);
        const activeComp = _.cloneDeep(computedDaActiveComp.value);
        const isSubject = activeComp.isSubject;

        const computedPreferredCasing = computed(() => userSettingsStore.PreferredCasing);
        const preferredCasing = computedPreferredCasing.value;

        return {
            store,
            activeComp,
            isSubject,
            preferredCasing,
        };
    },
    async mounted() {
        const modalElement = this.$refs.saleHistory;
        this.modal = ModalService.initialize(modalElement, this);
        ModalService.size(modalElement, 'auto');
        ModalService.open(this.modal);

        // initialize
        await this.initialize();
    },
    methods: {
        async initialize() {
            this.isLoading = true;

            const salesHistoryProperty = await SalesHistoryManager.getByCompAddress(this.activeComp.address);
            this.saleHistoryList = _.cloneDeep(salesHistoryProperty.salesHistory);
            this.saleHistoryFetched = true;

            const salesHistorySelections = await SalesHistoryManager.getSelectionsByCompAddress(this.activeComp.address);
            this.excludeNonArmsLength = salesHistorySelections.excludeNonArmsLength;
            this.includeInAddenda = salesHistorySelections.includeInAddenda;
            this.includeAnalysis = salesHistorySelections.includeAnalysis;
            this.isLoading = false;

            if (this.hasHistoryData()) {
                this.appendAdditionalProperties();
                this.applyTranslations();
                this.sortDates();
                if (salesHistorySelections.chronologyNumbers.length === 0) {
                    this.computeSelectedRows();
                } else {
                    this.selectRowsFromSaved(salesHistorySelections);
                }
                this.updateAnalysis();
            }

            LogUsageManager.launchedSalesHistory();
        },
        /**
         * Format the sale date or recorded date as MM/DD/YYYY or 'N/A'
         */
        formatDate(dateString: string) {
            if (dateString) {
                const dateTime = DateTimeUtils.fromFormat(dateString, 'YYYY-MM-DD');
                const formatted = DateTimeUtils.toFormat(dateTime, 'MM/DD/YYYY', 'N/A');
                return formatted;
            }
            return 'N/A';
        },
        hasHistoryData() {
            return Utils.objectSize(this.saleHistoryList) > 0 ? true : false;
        },
        allowInteraction() {
            return this.hasHistoryData() && this.saleHistoryFetched;
        },
        applyTranslations() {
            Object.keys(this.saleHistoryList).forEach((key, index) => {
                //this.saleHistoryList[key].armsLength = this.saleHistoryList[key].armsLength ? 'Yes' : 'No';
                this.saleHistoryList[key] = this.applyCasing(this.saleHistoryList[key]);
            });
        },
        applyCasing(obj: ISalesHistoryProperty) {
            const entries = Object.entries(obj);
            const casedObj = entries.map(([key, val]) => [key, typeof val === 'string' ? GridColumnUtils.transferValueCasing(val, this.preferredCasing) : val]);
            return Object.fromEntries(casedObj);
        },
        translationArmsLength(armsLength: boolean) {
            const value = armsLength ? 'Yes' : 'No';
            return GridColumnUtils.transferValueCasing(value, this.preferredCasing);
        },
        appendAdditionalProperties() {
            this.saleHistoryList = this.saleHistoryList.map((obj: ISalesHistoryProperty) => ({ ...obj, isSelected: false }));
            this.saleHistoryList = this.saleHistoryList.map((obj: ISalesHistoryProperty) => ({ ...obj, isDisabled: false }));
        },
        computeSelectedRows() {
            const saleHistoryYears = this.getSaleHistoryValue();
            const nonArmsLength = this.getNonArmsLengthValue();

            const currentDate = (this as any).$moment(new Date()).format('YYYY-MM-DD');
            const currentDateMinusSaleHistoryYears = (this as any).$moment(new Date()).subtract(saleHistoryYears, 'years').format('YYYY-MM-DD');

            Object.keys(this.saleHistoryList).forEach((key, index) => {
                this.saleHistoryList[key].isSelected = false;
                const date = this.chronologyNumberToDate(this.saleHistoryList[key].chronologyNumber);
                const isBetween = (this as any).$moment(date).isBetween(currentDateMinusSaleHistoryYears, currentDate, undefined, '[]');
                if (isBetween) {
                    if (nonArmsLength && this.saleHistoryList[key].armsLength) {
                        this.saleHistoryList[key].isSelected = true;
                    } else if (!nonArmsLength) {
                        this.saleHistoryList[key].isSelected = true;
                    }
                }
            });
        },
        selectRowsFromSaved(selections: ISalesHistorySelections) {
            Object.keys(this.saleHistoryList).forEach((key, index) => {
                const foundIdx = selections.chronologyNumbers.findIndex((cn) => cn === this.saleHistoryList[key].chronologyNumber);
                this.saleHistoryList[key].isSelected = foundIdx >= 0;
            });
        },
        isCheckBoxChecked(isSelected: boolean, isDisabled: boolean) {
            if (isDisabled) {
                return false;
            } else {
                return isSelected ? true : false;
            }
        },
        isCheckBoxDisabled(isSelected: boolean, isDisabled: boolean, armsLength: boolean) {
            if (this.getNonArmsLengthValue()) {
                if (isDisabled) {
                    return true;
                } else if (!isSelected && !armsLength) {
                    return true;
                } else if (!armsLength) {
                    return true;
                }
            }
            return false;
        },
        getSaleHistoryValue() {
            const saleHistoryYearsElement = document.getElementById('saleHistoryYears') as HTMLInputElement;
            return saleHistoryYearsElement.value !== 'all' ? saleHistoryYearsElement.value : 200;
        },
        getNonArmsLengthValue() {
            const nonArmsLengthElement = document.getElementById('nonArmsLength') as HTMLInputElement;
            return nonArmsLengthElement.checked ? true : false;
        },
        sortDates() {
            this.saleHistoryList.sort((a: { chronologyNumber: number }, b: { chronologyNumber: number }) => (a.chronologyNumber < b.chronologyNumber ? 1 : -1));
        },
        formatNumber(value: any) {
            return Utils.formatNumberToCommas(value);
        },
        defaultSelected(type: string) {
            if (type === 'subject') {
                return this.isSubject ? true : false;
            } else if (type === 'comp') {
                return !this.isSubject ? true : false;
            }
            return false;
        },
        onCheckBoxChange(event: any, index: number) {
            this.saleHistoryList[index].isSelected = event.target.checked;
            this.updateAnalysis();
        },
        onSaleYearChange() {
            this.computeSelectedRows();
            this.updateAnalysis();
        },
        onNonArmsLengthChange(event: any) {
            this.excludeNonArmsLength = event.target.checked;

            Object.keys(this.saleHistoryList).forEach((key, index) => {
                if (event.target.checked) {
                    if (!this.saleHistoryList[key].armsLength) {
                        this.saleHistoryList[index].isSelected = false;
                        this.saleHistoryList[index].isDisabled = true;
                    }
                } else {
                    this.saleHistoryList[index].isDisabled = false;
                }
            });
            this.updateAnalysis();
        },
        onIncludeInAddendaChange(event: any) {
            this.includeInAddenda = event.target.checked;
        },
        onIncludeAnalysisChange(event: any) {
            this.includeAnalysis = event.target.checked;
            this.updateAnalysis();
        },
        chronologyNumberToDate(chronologyNumber: string) {
            const dateString = chronologyNumber.substring(0, 8);
            const dateYear = dateString.substring(0, 4);
            const dateMonth = dateString.substring(4, 6);
            const dateDay = dateString.substring(6, 8);
            return `${dateYear}-${dateMonth}-${dateDay}`;
        },
        updateTransferColumn() {
            const selectedRows = this.getSelectedRows();
            this.processRowSelection(selectedRows);
        },
        getSelectedRows() {
            const result = this.saleHistoryList.filter((x: { isSelected: boolean }) => x.isSelected);
            return result;
        },
        processRowSelection(selectedRows: ISaleData[]) {
            let row: ISaleData = newSaleData();
            // If subject, Update the Transfer to Form column with the most recent selected transaction in the Sale History table
            if (this.isSubject) {
                if (selectedRows[0]) {
                    row = selectedRows[0];
                }
                // If not subject, Update the Transfer to Form column with the second most recent selected transaction in the Sale History table
            } else {
                if (selectedRows[1]) {
                    row = selectedRows[1];
                }
            }

            if (row.saleDate !== null) {
                const date = DateTimeUtils.fromFormat(row.saleDate, 'YYYY-MM-DD');
                const formatted = DateTimeUtils.toFormat(date, 'MM/DD/YYYY', '');
                const request = newUpsertCompDataRequestByFieldName(this.activeComp, 'HS_PRIOR1');
                request.sourceInitial = SourceDataInitials.UserOverride;
                request.value = formatted;
                request.description = 'SaleHistory.processRowSelection()';
                this.store.commit(Mutations.CompAddresses.UpsertCompSourceDataByFieldName, request);
            }

            if (row.price !== null) {
                const request = newUpsertCompDataRequestByFieldName(this.activeComp, 'HS_PRIOR2');
                request.sourceInitial = SourceDataInitials.UserOverride;
                request.value = `${row.price}`;
                request.description = 'SaleHistory.processRowSelection()';
                this.store.commit(Mutations.CompAddresses.UpsertCompSourceDataByFieldName, request);
            }
        },
        updateAnalysis() {
            const chronologyNumbers = this.getSelectedChronologyNumbers();
            if (chronologyNumbers.length > 0) {
                this.analysisOfPriorSales = SalesHistoryManager.generateAnalysisText(this.activeComp.address, this.saleHistoryList, chronologyNumbers);
            } else {
                this.analysisOfPriorSales = '';
            }
        },
        getSelectedChronologyNumbers(): string[] {
            const chronologyNumbers: string[] = [];
            Object.keys(this.saleHistoryList).forEach((key, index) => {
                if (this.saleHistoryList[index].isSelected) {
                    chronologyNumbers.push(this.saleHistoryList[index].chronologyNumber);
                }
            });
            return chronologyNumbers;
        },
        async saveSelections() {
            const address1UnitNoZipCode = CompAddressUtils.getAddress1UnitNoAndZipCode(this.activeComp.address);
            const selections = newSalesHistorySelections(address1UnitNoZipCode);

            selections.chronologyNumbers = this.getSelectedChronologyNumbers();
            selections.excludeNonArmsLength = this.excludeNonArmsLength;
            selections.includeInAddenda = this.includeInAddenda;
            selections.includeAnalysis = this.includeAnalysis;

            await SalesHistoryManager.saveSelections(selections);

            // Get selected row to update transfer column
            this.updateTransferColumn();

            LogUsageManager.savedSalesHistory();

            ModalService.hide(this.modal);
        },
    },
});
